import React, { useState, useEffect } from 'react';
import './App.css';
import Dashboard from './components/Dashboard';
import SinglePdfUpload from './components/SinglePdfUpload';
import AnalysisUpload from './components/AnalysisUpload';
import ResidentialRefiUpload from './components/ResidentialRefiUpload';
import GeminiExtract from './components/GeminiExtract';
import AccelerateIntegration from './components/AccelerateIntegration';
import CaseDataForm from './components/CaseDataForm';
import Header from './components/Header';
import DataTraceSearch from './components/DataTraceSearch';
import DataTraceResults from './components/DataTraceResults';
import DataTraceConfig from './components/DataTraceConfig';
import DataTraceAPITester from './components/DataTraceAPITester';
import FileRenamer from './components/FileRenamer';
import TitlePointAPITester from './components/TitlePointAPITester';
import TitlePointWorkflow from './components/TitlePointWorkflow';
import TitlePoint from './components/TitlePoint';
import Login from './components/Login';
import { searchPropertyByAPN, isConfigured } from './services/datatraceApi';

function App() {
  const [currentTask, setCurrentTask] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState(['openai', 'claude', 'gemini']);
  const [dataTraceResults, setDataTraceResults] = useState(null);
  const [dataTraceSearchParams, setDataTraceSearchParams] = useState(null);
  const [dataTraceConfigured, setDataTraceConfigured] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [refiInstructions, setRefiInstructions] = useState('');
  const [refiSelectedModels, setRefiSelectedModels] = useState(['claude', 'openai', 'gemini']);
  const [treasurerContact, setTreasurerContact] = useState(null);
  const [deedsContact, setDeedsContact] = useState(null);
  const [lenderContact, setLenderContact] = useState(null); // Lender auto-search result
  const [loanOfficerContact, setLoanOfficerContact] = useState(null); // Loan officer auto-search result
  const [processorContact, setProcessorContact] = useState(null); // Processor auto-search result
  const [dataTraceAutoResult, setDataTraceAutoResult] = useState(null); // Automatic DataTrace result

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/auth/check', {
          credentials: 'include'
        });
        const data = await response.json();
        setIsAuthenticated(data.authenticated);
      } catch (err) {
        console.error('Auth check failed:', err);
        setIsAuthenticated(false);
      } finally {
        setAuthChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Sync with browser history and handle back/forward navigation
  useEffect(() => {
    // On initial load, check if there's a path in the URL
    const path = window.location.pathname;
    if (path && path !== '/' && isAuthenticated) {
      const taskFromPath = path.substring(1);
      if (taskFromPath) {
        setCurrentTask(taskFromPath);
      }
    }

    // Listen for browser back/forward button clicks
    const handlePopState = (event) => {
      if (event.state && event.state.task) {
        setCurrentTask(event.state.task);
      } else {
        // Going back to home/dashboard
        setCurrentTask(null);
        setUploadedFile(null);
        setCaseData(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAuthenticated]);

  // Watch for DataTrace results and trigger government contact search if county is available
  useEffect(() => {
    // Only trigger if we have DataTrace results with a county
    if (dataTraceAutoResult?.property?.county) {
      const county = dataTraceAutoResult.property.county;

      // Don't trigger if we already have contacts loading or loaded
      if (treasurerContact || deedsContact) {
        return;
      }

      console.log('DataTrace returned county:', county, '- triggering government contact search');

      // Set loading state for both contacts
      setTreasurerContact({ loading: true, found: false, contact: null, error: null });
      setDeedsContact({ loading: true, found: false, contact: null, error: null });

      // Fetch both contacts in parallel
      const fetchTreasurer = async () => {
        try {
          const searchTerm = `${county} County Treasurer`;
          const contactResponse = await fetch(`/api/analysis/search-contact?search_term=${encodeURIComponent(searchTerm)}`, {
            credentials: 'include'
          });

          if (!contactResponse.ok) {
            const errorText = await contactResponse.text();
            console.error('County treasurer search failed:', errorText);
            throw new Error(`HTTP ${contactResponse.status}: ${errorText}`);
          }

          const contactData = await contactResponse.json();
          setTreasurerContact(contactData);
          console.log('Treasurer contact:', contactData);
        } catch (error) {
          console.error('Failed to fetch treasurer contact:', error);
          setTreasurerContact({
            loading: false,
            found: false,
            contact: null,
            error: error.message || 'Failed to search for county treasurer contact'
          });
        }
      };

      const fetchDeeds = async () => {
        try {
          const searchTerm = `${county} County Register of Deeds`;
          const contactResponse = await fetch(`/api/analysis/search-contact?search_term=${encodeURIComponent(searchTerm)}`, {
            credentials: 'include'
          });

          if (!contactResponse.ok) {
            const errorText = await contactResponse.text();
            console.error('Register of Deeds search failed:', errorText);
            throw new Error(`HTTP ${contactResponse.status}: ${errorText}`);
          }

          const contactData = await contactResponse.json();
          setDeedsContact(contactData);
          console.log('Deeds contact:', contactData);
        } catch (error) {
          console.error('Failed to fetch deeds contact:', error);
          setDeedsContact({
            loading: false,
            found: false,
            contact: null,
            error: error.message || 'Failed to search for register of deeds contact'
          });
        }
      };

      // Execute both searches in parallel
      Promise.all([fetchTreasurer(), fetchDeeds()]);
    }
  }, [dataTraceAutoResult, treasurerContact, deedsContact]);

  const handleTaskSelect = (taskId) => {
    setCurrentTask(taskId);
    setError(null);
    // Push to browser history so back button works
    window.history.pushState({ task: taskId }, '', `/${taskId}`);
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleFileUpload = async (file, metadata = null) => {
    setUploadedFile(file);
    setLoading(true);

    // Handle residential refinance workflow with metadata (models, instructions)
    if (metadata && metadata.models && currentTask === 'lmcu-refi') {
      setRefiInstructions(metadata.instructions || '');
      setRefiSelectedModels(metadata.models);

      try {
        // Create FormData to send the PDF file
        const formData = new FormData();
        formData.append('file', file);
        formData.append('models', JSON.stringify(metadata.models));
        formData.append('instructions', metadata.instructions || '');

        console.log('Uploading PDF for residential refinance analysis:', file.name);
        console.log('Selected models:', metadata.models);
        console.log('Instructions:', metadata.instructions);

        // Send to our proxy endpoint which forwards to Python backend
        const response = await fetch('/api/analysis/analyze-pdf', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Analysis failed');
        }

        const analysisResult = await response.json();
        console.log('Analysis result:', analysisResult);

        // Backend now returns data.openai, data.claude, data.gemini with fully formatted data
        console.log('DEBUG - analysisResult.data structure:', analysisResult.data);
        console.log('DEBUG - Has openai?', !!analysisResult.data?.openai);
        console.log('DEBUG - Has claude?', !!analysisResult.data?.claude);
        console.log('DEBUG - Has gemini?', !!analysisResult.data?.gemini);

        let convertedData;
        if (analysisResult.data && analysisResult.data.openai && analysisResult.data.claude && analysisResult.data.gemini) {
          // New format: data contains per-model formatted data
          console.log('DEBUG - Using NEW per-model format');
          convertedData = {
            openai: analysisResult.data.openai,
            claude: analysisResult.data.claude,
            gemini: analysisResult.data.gemini,
            internalId: analysisResult.internalId
          };
        } else if (analysisResult.data && typeof analysisResult.data === 'object') {
          // Old consensus format - duplicate across models (for backwards compatibility)
          convertedData = {
            openai: analysisResult.data,
            claude: analysisResult.data,
            gemini: analysisResult.data,
            internalId: analysisResult.internalId,
            _isConsensusData: true
          };
        } else {
          // Fallback for unexpected format
          convertedData = {
            openai: {},
            claude: {},
            gemini: {},
            internalId: analysisResult.internalId
          };
        }

        setCaseData(convertedData);
        setLoading(false);

        // Auto-trigger DataTrace analysis if enabled
        if (metadata.enableDataTrace) {
          // Get data from the first available model (prefer openai, then claude, then gemini)
          const firstModelData = analysisResult.data?.openai || analysisResult.data?.claude || analysisResult.data?.gemini || analysisResult.data || {};
          const address = firstModelData.PropertyAddress1;
          const city = firstModelData.PropertyCity;
          const state = firstModelData.PropertyState;
          const zip = firstModelData.PropertyZip;
          const county = firstModelData.PropertyCounty;
          const apn = firstModelData.PropertyAPN;

          // Only trigger if we have at least address, city, and state
          if (address && city && state) {
            console.log('Auto-triggering DataTrace analysis...');

            // Run DataTrace in background (non-blocking)
            (async () => {
              try {
                const searchParams = {
                  address,
                  city,
                  state,
                  zip: zip || '',
                  county: county || 'Kent',
                  countyFips: '', // Will be auto-filled by backend if needed
                  apn: apn || ''
                };

                const dataTraceResponse = await searchPropertyByAPN(searchParams);
                console.log('Auto DataTrace result:', dataTraceResponse);
                setDataTraceAutoResult(dataTraceResponse);
              } catch (error) {
                console.error('Auto DataTrace failed (non-blocking):', error);
                // Don't block UI if DataTrace fails
                setDataTraceAutoResult(null);
              }
            })();
          }
        }

        // Start loading both contacts in background if county is available from ANY model
        // Check all three models - use the first non-empty county found
        const openaiCounty = analysisResult.data?.openai?.PropertyCounty;
        const claudeCounty = analysisResult.data?.claude?.PropertyCounty;
        const geminiCounty = analysisResult.data?.gemini?.PropertyCounty;
        const county = openaiCounty || claudeCounty || geminiCounty;

        if (county && county.trim()) {
          // Set loading state for both contacts
          setTreasurerContact({ loading: true, found: false, contact: null, error: null });
          setDeedsContact({ loading: true, found: false, contact: null, error: null });

          // Fetch both contacts in parallel
          const fetchTreasurer = async () => {
            try {
              const searchTerm = `${county} County Treasurer`;
              const contactResponse = await fetch(`/api/analysis/search-contact?search_term=${encodeURIComponent(searchTerm)}`, {
                credentials: 'include'
              });

              if (!contactResponse.ok) {
                const errorText = await contactResponse.text();
                console.error('County treasurer search failed:', errorText);
                throw new Error(`HTTP ${contactResponse.status}: ${errorText}`);
              }

              const contactData = await contactResponse.json();
              setTreasurerContact(contactData);
              console.log('Treasurer contact:', contactData);
            } catch (error) {
              console.error('Failed to fetch treasurer contact:', error);
              setTreasurerContact({
                loading: false,
                found: false,
                contact: null,
                error: error.message || 'Failed to search for county treasurer contact'
              });
            }
          };

          const fetchDeeds = async () => {
            try {
              const searchTerm = `${county} County Register of Deeds`;
              const contactResponse = await fetch(`/api/analysis/search-contact?search_term=${encodeURIComponent(searchTerm)}`, {
                credentials: 'include'
              });

              if (!contactResponse.ok) {
                const errorText = await contactResponse.text();
                console.error('Register of Deeds search failed:', errorText);
                throw new Error(`HTTP ${contactResponse.status}: ${errorText}`);
              }

              const contactData = await contactResponse.json();
              setDeedsContact(contactData);
              console.log('Deeds contact:', contactData);
            } catch (error) {
              console.error('Failed to fetch deeds contact:', error);
              setDeedsContact({
                loading: false,
                found: false,
                contact: null,
                error: error.message || 'Failed to search for register of deeds contact'
              });
            }
          };

          // Execute both searches in parallel
          Promise.all([fetchTreasurer(), fetchDeeds()]);
        }

        // Start loading lender contact in background if lender name is available from ANY model
        // Check all three models - use the first non-empty lender name found
        const openaiLender = analysisResult.data?.openai?.MortgageeName;
        const claudeLender = analysisResult.data?.claude?.MortgageeName;
        const geminiLender = analysisResult.data?.gemini?.MortgageeName;
        const lenderName = openaiLender || claudeLender || geminiLender;

        if (lenderName && lenderName.trim()) {
          // Set loading state for lender contact
          setLenderContact({ loading: true, found: false, lender: null, error: null });

          // Fetch lender contact
          const fetchLender = async () => {
            try {
              const lenderResponse = await fetch(`/api/softpro/lenders/search?name=${encodeURIComponent(lenderName)}`, {
                credentials: 'include'
              });

              if (!lenderResponse.ok) {
                const errorText = await lenderResponse.text();
                console.error('Lender search failed:', errorText);
                throw new Error(`HTTP ${lenderResponse.status}: ${errorText}`);
              }

              const lenderData = await lenderResponse.json();
              setLenderContact(lenderData);
              console.log('Lender contact:', lenderData);
            } catch (error) {
              console.error('Failed to fetch lender contact:', error);
              setLenderContact({
                loading: false,
                found: false,
                lender: null,
                error: error.message || 'Failed to search for lender contact'
              });
            }
          };

          // Execute lender search
          fetchLender();
        }

        // Start loading loan officer contact if name is available from ANY model
        const openaiOfficer = analysisResult.data?.openai?.LoanOfficerName;
        const claudeOfficer = analysisResult.data?.claude?.LoanOfficerName;
        const geminiOfficer = analysisResult.data?.gemini?.LoanOfficerName;
        const loanOfficerName = openaiOfficer || claudeOfficer || geminiOfficer;

        if (loanOfficerName && loanOfficerName.trim()) {
          // Set loading state for loan officer contact
          setLoanOfficerContact({ loading: true, found: false, people: [], error: null });

          // Fetch loan officer contact
          const fetchLoanOfficer = async () => {
            try {
              const officerResponse = await fetch(`/api/softpro/people/search?name=${encodeURIComponent(loanOfficerName)}`, {
                credentials: 'include'
              });

              if (!officerResponse.ok) {
                const errorText = await officerResponse.text();
                console.error('Loan officer search failed:', errorText);
                throw new Error(`HTTP ${officerResponse.status}: ${errorText}`);
              }

              const officerData = await officerResponse.json();
              setLoanOfficerContact(officerData);
              console.log('Loan officer contact:', officerData);
            } catch (error) {
              console.error('Failed to fetch loan officer contact:', error);
              setLoanOfficerContact({
                loading: false,
                found: false,
                people: [],
                error: error.message || 'Failed to search for loan officer contact'
              });
            }
          };

          // Execute loan officer search
          fetchLoanOfficer();
        }

        // Start loading processor contact if name is available from ANY model
        const openaiProcessor = analysisResult.data?.openai?.ProcessorName;
        const claudeProcessor = analysisResult.data?.claude?.ProcessorName;
        const geminiProcessor = analysisResult.data?.gemini?.ProcessorName;
        const processorName = openaiProcessor || claudeProcessor || geminiProcessor;

        if (processorName && processorName.trim()) {
          // Set loading state for processor contact
          setProcessorContact({ loading: true, found: false, people: [], error: null });

          // Fetch processor contact
          const fetchProcessor = async () => {
            try {
              const processorResponse = await fetch(`/api/softpro/people/search?name=${encodeURIComponent(processorName)}`, {
                credentials: 'include'
              });

              if (!processorResponse.ok) {
                const errorText = await processorResponse.text();
                console.error('Processor search failed:', errorText);
                throw new Error(`HTTP ${processorResponse.status}: ${errorText}`);
              }

              const processorData = await processorResponse.json();
              setProcessorContact(processorData);
              console.log('Processor contact:', processorData);
            } catch (error) {
              console.error('Failed to fetch processor contact:', error);
              setProcessorContact({
                loading: false,
                found: false,
                people: [],
                error: error.message || 'Failed to search for processor contact'
              });
            }
          };

          // Execute processor search
          fetchProcessor();
        }
      } catch (error) {
        console.error('Analysis error:', error);
        alert(`Failed to analyze PDF: ${error.message}`);
        setLoading(false);
      }
      return;
    }

    // Legacy handling for analysis result directly passed
    if (metadata && metadata.data && currentTask === 'lmcu-refi') {
      // Convert Python backend result to our format
      const convertedData = {
        openai: metadata.data,
        claude: metadata.data,
        gemini: metadata.data
      };

      setCaseData(convertedData);
      setLoading(false);
      return;
    }

    // Simulate API call to Sunny AI backend
    setTimeout(() => {
      // Mock data varies by order type
      let mockCaseData;

      if (currentTask === 'lmcu-refi') {
        // Mock data for Residential Refinance
        mockCaseData = {
          openai: {
            propertyAddress: '456 Oak Avenue, Grand Rapids, MI 49503',
            propertyId: 'APN-41-14-30-178-004',
            ownerName: 'Michael R. Johnson & Lisa M. Johnson',
            orderDate: '2025-12-05',
            requestDate: '2025-12-01',
            borrowerName: 'Michael R. Johnson & Lisa M. Johnson',
            loanAmount: '$285,000',
            currentLoanBalance: '$312,500',
            lenderName: 'Lake Michigan Credit Union',
            loanNumber: 'REFI-2025-78542',
            loanOfficer: 'David Thompson',
            closingDate: '2025-12-20',
            transactionType: 'Refinance',
            loanPurpose: 'Rate/Term Refinance'
          },
          claude: {
            propertyAddress: '456 Oak Avenue, Grand Rapids, MI 49503',
            propertyId: 'APN-41-14-30-178-004',
            ownerName: 'Michael R. Johnson & Lisa M. Johnson',
            orderDate: '2025-12-05',
            requestDate: '2025-12-01',
            borrowerName: 'Michael R. Johnson & Lisa M. Johnson',
            loanAmount: '$285,000',
            currentLoanBalance: '$312,500',
            lenderName: 'Lake Michigan Credit Union',
            loanNumber: 'REFI-2025-78542',
            loanOfficer: 'David Thompson',
            closingDate: '2025-12-20',
            transactionType: 'Refinance',
            loanPurpose: 'Rate/Term Refinance'
          },
          gemini: {
            propertyAddress: '456 Oak Avenue, Grand Rapids, MI 49503',
            propertyId: 'APN-41-14-30-178-004',
            ownerName: 'Michael R. Johnson & Lisa M. Johnson',
            orderDate: '2025-12-05',
            requestDate: '2025-12-01',
            borrowerName: 'Michael R. Johnson & Lisa M. Johnson',
            loanAmount: '$285,000',
            currentLoanBalance: '$312,500',
            lenderName: 'Lake Michigan Credit Union',
            loanNumber: 'REFI-2025-78542',
            loanOfficer: 'David Thompson',
            closingDate: '2025-12-20',
            transactionType: 'Refinance',
            loanPurpose: 'Rate/Term Refinance'
          }
        };
      } else {
        // Mock data for Purchase Agreement
        mockCaseData = {
          openai: {
            propertyAddress: '123 Main Street, Los Angeles, CA 90001',
            propertyId: 'APN-12345-678',
            ownerName: 'John Smith',
            orderDate: '2025-11-24',
            requestDate: '2025-11-20',
            buyerName: 'Jane Doe',
            sellerName: 'John Smith',
            loanAmount: '$450,000',
            purchasePrice: '$550,000',
            lenderName: 'First National Bank',
            escrowNumber: 'ESC-2025-001234',
            agentName: 'Sarah Johnson',
            agentCompany: 'Prime Realty Group',
            closingDate: '2025-12-15'
          },
          claude: {
            propertyAddress: '123 Main Street, Los Angeles, CA 90001',
            propertyId: 'APN-12345-678',
            ownerName: 'John Smith',
            orderDate: '2025-11-24',
            requestDate: '2025-11-20',
            buyerName: 'Jane Doe',
            sellerName: 'John Smith',
            loanAmount: '$450,000',
            purchasePrice: '$550,000',
            lenderName: 'First National Bank',
            escrowNumber: 'ESC-2025-001234',
            agentName: 'Sarah Johnson',
            agentCompany: 'Prime Realty Group',
            closingDate: '2025-12-15'
          },
          gemini: {
            propertyAddress: '123 Main Street, Los Angeles, CA 90001',
            propertyId: 'APN-12345-678',
            ownerName: 'John Smith',
            orderDate: '2025-11-24',
            requestDate: '2025-11-20',
            buyerName: 'Jane Doe',
            sellerName: 'John Smith',
            loanAmount: '$450,000',
            purchasePrice: '$550,000',
            lenderName: 'First National Bank',
            escrowNumber: 'ESC-2025-001234',
            agentName: 'Sarah Johnson',
            agentCompany: 'Prime Realty Group',
            closingDate: '2025-12-15'
          }
        };
      }

      setCaseData(mockCaseData);
      setLoading(false);
    }, 2000);
  };

  const handleProviderToggle = (provider) => {
    setSelectedProviders(prev => {
      if (prev.includes(provider)) {
        return prev.filter(p => p !== provider);
      } else {
        return [...prev, provider];
      }
    });
  };

  const handleBackToDashboard = () => {
    setCurrentTask(null);
    setUploadedFile(null);
    setCaseData(null);
    setLoading(false);
    setDataTraceResults(null);
    setDataTraceSearchParams(null);
    setError(null);
    setTreasurerContact(null);
    setDeedsContact(null);
    setLenderContact(null);
    setLoanOfficerContact(null);
    setProcessorContact(null);
    setDataTraceAutoResult(null); // Clear auto DataTrace result
    // Push to browser history so back button works
    window.history.pushState({ task: null }, '', '/');
  };

  const handleDataTraceConfigured = (useMock = false) => {
    setDataTraceConfigured(true);
    setUseMockData(useMock);
  };

  const handleBackToUpload = () => {
    setUploadedFile(null);
    setCaseData(null);
    setLoading(false);
    setTreasurerContact(null);
    setDeedsContact(null);
    setLenderContact(null);
    setLoanOfficerContact(null);
    setProcessorContact(null);
    setDataTraceAutoResult(null); // Clear auto DataTrace result
  };

  const handleDataTraceSearch = async (searchParams) => {
    setDataTraceSearchParams(searchParams);
    setLoading(true);
    setError(null);

    // Use mock data if configured to do so
    if (useMockData) {
      setTimeout(() => {
        const mockDataTraceResults = {
          property: {
            address: '123 Main Street',
            cityStateZip: 'Los Angeles, CA 90001',
            county: searchParams.county,
            apn: searchParams.apn,
            legalDescription: 'LOT 15 OF TRACT NO. 12345, IN THE CITY OF LOS ANGELES, COUNTY OF LOS ANGELES, STATE OF CALIFORNIA, AS PER MAP RECORDED IN BOOK 200, PAGES 45-48 OF MAPS, IN THE OFFICE OF THE COUNTY RECORDER OF SAID COUNTY.',
            propertyType: 'Single Family Residence',
            landUse: 'Residential',
            zoning: 'R-1',
            lotSize: '7,500 sq ft',
            yearBuilt: '1985',
            livingArea: '2,150 sq ft',
            bedsBaths: '4 BD / 2.5 BA'
          },
          ownership: {
            currentOwner: {
              name: 'John A. Smith & Mary B. Smith',
              vesting: 'Joint Tenants with Right of Survivorship',
              mailingAddress: '123 Main Street, Los Angeles, CA 90001',
              acquisitionDate: '03/15/2018',
              acquisitionPrice: '$485,000',
              documentNumber: '2018-0234567'
            },
            history: [
              { date: '03/15/2018', owner: 'John A. Smith & Mary B. Smith', transactionType: 'Grant Deed', price: '$485,000' },
              { date: '06/22/2010', owner: 'Robert J. Williams', transactionType: 'Grant Deed', price: '$320,000' },
              { date: '09/10/2003', owner: 'ABC Development LLC', transactionType: 'Grant Deed', price: '$225,000' },
              { date: '01/15/1998', owner: 'First National Bank', transactionType: 'Trustee Sale', price: '$180,000' }
            ]
          },
          tax: {
            taxYear: '2024-2025',
            totalTax: '$6,234.56',
            status: 'Paid',
            firstInstallment: '$3,117.28 (Paid)',
            secondInstallment: '$3,117.28 (Paid)',
            taxRateArea: '00-067',
            assessedLand: '$285,000',
            assessedImprovement: '$215,000',
            totalAssessed: '$500,000',
            marketValue: '$685,000',
            exemptions: [
              { type: 'Homeowner Exemption', amount: '$7,000' }
            ]
          },
          documents: [
            { recordingDate: '03/15/2018', documentType: 'Grant Deed', documentNumber: '2018-0234567', grantor: 'Robert J. Williams', grantee: 'John A. Smith et al' },
            { recordingDate: '03/15/2018', documentType: 'Deed of Trust', documentNumber: '2018-0234568', grantor: 'John A. Smith et al', grantee: 'First National Bank' },
            { recordingDate: '06/22/2010', documentType: 'Grant Deed', documentNumber: '2010-0567891', grantor: 'ABC Development LLC', grantee: 'Robert J. Williams' },
            { recordingDate: '06/22/2010', documentType: 'Deed of Trust', documentNumber: '2010-0567892', grantor: 'Robert J. Williams', grantee: 'Wells Fargo Bank' },
            { recordingDate: '03/10/2018', documentType: 'Reconveyance', documentNumber: '2018-0198765', grantor: 'Wells Fargo Bank', grantee: 'Robert J. Williams' }
          ],
          liens: [
            { type: 'Deed of Trust', amount: '$388,000', holder: 'First National Bank', recordedDate: '03/15/2018' }
          ]
        };

        setDataTraceResults(mockDataTraceResults);
        setLoading(false);
      }, 2000);
      return;
    }

    // Use real DataTrace API
    try {
      const results = await searchPropertyByAPN(searchParams);
      setDataTraceResults(results);
    } catch (err) {
      console.error('DataTrace API Error:', err);
      setError(err.message || 'Failed to retrieve property data from DataTrace');
    } finally {
      setLoading(false);
    }
  };

  const handleNewDataTraceSearch = () => {
    setDataTraceResults(null);
    setDataTraceSearchParams(null);
    setError(null);
  };

  // Check if we should show config screen for DataTrace
  const showDataTraceConfig = currentTask === 'datatrace' && !dataTraceConfigured && !isConfigured();

  // Generate breadcrumbs based on current state
  const getBreadcrumbs = () => {
    const breadcrumbs = [
      { label: 'Home', onClick: currentTask ? handleBackToDashboard : null }
    ];

    if (!currentTask) {
      return breadcrumbs;
    }

    // Task name mappings
    const taskNames = {
      'single-pdf': 'Purchase Agreement',
      'lmcu-refi': 'Residential Refinance',
      'file-renamer': 'File Renamer',
      'gemini-extract': 'Gemini Extraction',
      'datatrace-tester': 'DataTrace API Tester',
      'datatrace': 'DataTrace Search',
      'titlepoint': 'TitlePoint',
      'accelerate-integration': 'Accelerate Integration'
    };

    const taskName = taskNames[currentTask] || currentTask;

    // Add task level
    if (caseData && (currentTask === 'single-pdf' || currentTask === 'lmcu-refi')) {
      // On the case data form - show intermediate upload page as clickable
      breadcrumbs.push({ label: taskName, onClick: handleBackToUpload });
      breadcrumbs.push({ label: 'Case Data', onClick: null });
    } else if (dataTraceResults && currentTask === 'datatrace') {
      // On DataTrace results page
      breadcrumbs.push({ label: taskName, onClick: handleNewDataTraceSearch });
      breadcrumbs.push({ label: 'Results', onClick: null });
    } else {
      // On main task page
      breadcrumbs.push({ label: taskName, onClick: null });
    }

    return breadcrumbs;
  };

    // Show loading while checking auth
  if (authChecking) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <h2>Loading...</h2>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <Header breadcrumbs={getBreadcrumbs()} />

      <main className="main-content">
        {!currentTask && (
          <Dashboard onTaskSelect={handleTaskSelect} />
        )}

        {currentTask === 'single-pdf' && !uploadedFile && !loading && (
          <SinglePdfUpload
            onFileUpload={handleFileUpload}
            onBack={handleBackToDashboard}
          />
        )}

        {currentTask === 'lmcu-refi' && !uploadedFile && !loading && (
          <ResidentialRefiUpload
            onFileUpload={handleFileUpload}
            onBack={handleBackToDashboard}
          />
        )}

        {currentTask === 'file-renamer' && (
          <FileRenamer
            onBack={handleBackToDashboard}
          />
        )}

        {currentTask === 'gemini-extract' && (
          <GeminiExtract
            onBack={handleBackToDashboard}
          />
        )}

        {currentTask === 'datatrace-tester' && (
          <DataTraceAPITester
            onBack={handleBackToDashboard}
          />
        )}
        {currentTask === 'titlepoint' && (
          <TitlePoint
            onBack={handleBackToDashboard}
          />

        )}
        {currentTask === 'accelerate-integration' && (
          <AccelerateIntegration
            onBack={handleBackToDashboard}
          />
        )}



        {showDataTraceConfig && (
          <DataTraceConfig
            onConfigured={handleDataTraceConfigured}
            onBack={handleBackToDashboard}
          />
        )}

        {currentTask === 'datatrace' && !showDataTraceConfig && !dataTraceResults && !loading && !error && (
          <DataTraceSearch
            onSearch={handleDataTraceSearch}
            onBack={handleBackToDashboard}
            loading={loading}
          />
        )}

        {currentTask === 'datatrace' && error && !loading && (
          <div className="error-container">
            <div className="error-content-full">
              <div className="error-header">
                <div className="error-icon">⚠️</div>
                <h2>DataTrace API Error</h2>
              </div>
              <div className="error-details">
                <div className="error-section">
                  <h4>Error Message</h4>
                  <pre className="error-message-full">{typeof error === 'object' ? JSON.stringify(error, null, 2) : error}</pre>
                </div>
                {dataTraceSearchParams && (
                  <div className="error-section">
                    <h4>Search Parameters</h4>
                    <pre className="error-params">{JSON.stringify(dataTraceSearchParams, null, 2)}</pre>
                  </div>
                )}
              </div>
              <div className="error-actions">
                <button className="retry-button" onClick={() => setError(null)}>
                  Try Again
                </button>
                <button className="back-button" onClick={handleBackToDashboard}>
                  Back to Menu
                </button>
              </div>
            </div>
          </div>
        )}

        {currentTask === 'datatrace' && dataTraceResults && !loading && !error && (
          <DataTraceResults
            data={dataTraceResults}
            searchParams={dataTraceSearchParams}
            onBack={handleBackToDashboard}
            onNewSearch={handleNewDataTraceSearch}
          />
        )}

        {loading && currentTask === 'single-pdf' && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <h2>Analyzing Purchase Agreement...</h2>
            <p className="loading-text">Sunny AI is extracting case data from multiple AI providers</p>
            <p className="loading-subtext">OpenAI • Claude • Google Gemini</p>
          </div>
        )}

        {loading && currentTask === 'lmcu-refi' && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <h2>Analyzing Residential Refinance Documents...</h2>
            <p className="loading-text">Sunny AI is extracting refinance data from multiple AI providers</p>
            <p className="loading-subtext">OpenAI • Claude • Google Gemini</p>
          </div>
        )}

        {loading && currentTask === 'datatrace' && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <h2>Searching DataTrace...</h2>
            <p className="loading-text">Retrieving property information from DataTrace API</p>
            <p className="loading-subtext">Property Details • Ownership • Tax Info • Documents</p>
          </div>
        )}

        {caseData && !loading && (currentTask === 'single-pdf' || currentTask === 'lmcu-refi') && (
          <CaseDataForm
            caseData={caseData}
            selectedProviders={selectedProviders}
            onProviderToggle={handleProviderToggle}
            onReset={handleBackToUpload}
            fileName={uploadedFile.name}
            orderType={currentTask === 'lmcu-refi' ? 'refinance' : 'purchase'}
            refiInstructions={currentTask === 'lmcu-refi' ? refiInstructions : ''}
            refiSelectedModels={currentTask === 'lmcu-refi' ? refiSelectedModels : []}
            treasurerContact={treasurerContact}
            deedsContact={deedsContact}
            lenderContact={lenderContact}
            loanOfficerContact={loanOfficerContact}
            processorContact={processorContact}
            dataTraceAutoResult={dataTraceAutoResult}
          />
        )}
      </main>
    </div>
  );
}

export default App;
